package ru.chm.gradereport.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.GreenAccent

@Composable
fun AppCard(
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit,
) {
    val colors = GradeTheme.colors
    Column(
        modifier = modifier
            .background(colors.card, RoundedCornerShape(16.dp))
            .border(1.dp, colors.border, RoundedCornerShape(16.dp))
            .padding(16.dp),
        content = content,
    )
}

@Composable
fun GradientStatCard(
    title: String,
    value: String,
    subtitle: String = "",
    colors: List<Color>,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .background(Brush.linearGradient(colors), RoundedCornerShape(16.dp))
            .padding(16.dp),
    ) {
        Text(title, color = Color.White.copy(alpha = 0.85f), fontSize = 13.sp)
        Text(
            value,
            color = Color.White,
            fontSize = 22.sp,
            fontWeight = FontWeight.Bold,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.padding(top = 6.dp),
        )
        Spacer(Modifier.weight(1f))
        if (subtitle.isNotBlank()) {
            Text(
                subtitle,
                color = Color.White.copy(alpha = 0.8f),
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
    }
}

@Composable
fun PrimaryButton(
    text: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    onClick: () -> Unit,
) {
    Button(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier,
        colors = ButtonDefaults.buttonColors(containerColor = GreenAccent, contentColor = Color.White),
        shape = RoundedCornerShape(12.dp),
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 10.dp),
    ) {
        Text(
            text,
            fontWeight = FontWeight.SemiBold,
            maxLines = 1,
            softWrap = false,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
fun SecondaryButton(
    text: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    onClick: () -> Unit,
) {
    val onSurface = GradeTheme.colors.textPrimary
    OutlinedButton(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier,
        shape = RoundedCornerShape(12.dp),
        colors = ButtonDefaults.outlinedButtonColors(contentColor = onSurface),
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 10.dp),
    ) {
        Text(
            text,
            fontWeight = FontWeight.SemiBold,
            maxLines = 1,
            softWrap = false,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
fun AppTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    singleLine: Boolean = true,
    trailingIcon: (@Composable () -> Unit)? = null,
) {
    val colors = GradeTheme.colors
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(label) },
        modifier = modifier.fillMaxWidth(),
        singleLine = singleLine,
        trailingIcon = trailingIcon,
        shape = RoundedCornerShape(12.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = GreenAccent,
            unfocusedBorderColor = colors.border,
            focusedLabelColor = GreenAccent,
            unfocusedLabelColor = colors.textMuted,
            cursorColor = GreenAccent,
            focusedTextColor = colors.textPrimary,
            unfocusedTextColor = colors.textPrimary,
        ),
    )
}

/** Single selectable text node — safe on Web (do not nest SelectionContainers). */
@Composable
fun SelectableText(
    text: String,
    modifier: Modifier = Modifier,
    color: Color = GradeTheme.colors.textPrimary,
    fontSize: TextUnit = 14.sp,
    fontWeight: FontWeight? = null,
    lineHeight: TextUnit = TextUnit.Unspecified,
    maxLines: Int = Int.MAX_VALUE,
    overflow: TextOverflow = TextOverflow.Clip,
) {
    SelectionContainer(modifier = modifier) {
        Text(
            text = text,
            color = color,
            fontSize = fontSize,
            fontWeight = fontWeight,
            lineHeight = lineHeight,
            maxLines = maxLines,
            overflow = overflow,
        )
    }
}

@Composable
fun SectionTitle(text: String, modifier: Modifier = Modifier) {
    SelectableText(
        text = text,
        modifier = modifier,
        color = GradeTheme.colors.textPrimary,
        fontSize = 22.sp,
        fontWeight = FontWeight.Bold,
    )
}

@Composable
fun MutedText(text: String, modifier: Modifier = Modifier) {
    SelectableText(
        text = text,
        modifier = modifier,
        color = GradeTheme.colors.textMuted,
        fontSize = 14.sp,
    )
}

@Composable
fun ErrorBanner(message: String?, onDismiss: (() -> Unit)? = null) {
    if (message.isNullOrBlank()) return
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 12.dp)
            .background(GradeTheme.colors.errorBanner, RoundedCornerShape(12.dp))
            .padding(12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SelectableText(
            text = message,
            color = Color.White,
            lineHeight = 20.sp,
            modifier = Modifier.weight(1f),
        )
        if (onDismiss != null) {
            TextButton(onClick = onDismiss) { Text("Закрыть", color = Color.White) }
        }
    }
}

/** Full-width card for page-level failures (not found, broken link, etc.). */
@Composable
fun ErrorStateCard(
    title: String,
    message: String,
    modifier: Modifier = Modifier,
    hint: String? = null,
) {
    AppCard(modifier = modifier.fillMaxWidth()) {
        SelectableText(
            text = title,
            color = GradeTheme.colors.textPrimary,
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp,
        )
        SelectableText(
            text = message,
            color = GradeTheme.colors.textPrimary.copy(alpha = 0.9f),
            fontSize = 15.sp,
            lineHeight = 22.sp,
            modifier = Modifier.padding(top = 8.dp),
        )
        if (!hint.isNullOrBlank()) {
            MutedText(hint, modifier = Modifier.padding(top = 10.dp))
        }
    }
}

@Composable
fun LoadingBox(modifier: Modifier = Modifier) {
    Box(modifier = modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
        CircularProgressIndicator(color = GreenAccent)
    }
}

@Composable
fun TabChip(
    text: String,
    selected: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    val colors = GradeTheme.colors
    val shape = RoundedCornerShape(10.dp)
    val hover = rememberSoftHover()
    val wash = if (colors.isDark) Color.White else Color.Black
    val idleBg = if (selected) GreenAccent else Color.Transparent
    val (bg, border) = softFieldColors(
        state = hover,
        idleBg = idleBg,
        hoverBg = if (selected) GreenAccent else wash.copy(alpha = 0.08f),
        pressBg = if (selected) GreenAccent else wash.copy(alpha = 0.12f),
        idleBorder = if (selected) GreenAccent else colors.border,
        hoverBorder = if (selected) GreenAccent else colors.border.copy(alpha = 0.9f),
    )
    val fg = if (selected) Color.White else colors.textMuted

    Box(
        modifier = modifier
            .softHoverable(hover)
            .clip(shape)
            .background(bg, shape)
            .border(1.dp, border, shape)
            .clickable(
                indication = null,
                interactionSource = hover.interactionSource,
                onClick = onClick,
            )
            .padding(horizontal = 12.dp, vertical = 8.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(text, color = fg, fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium)
    }
}
